import { useEffect, useState } from "react";
import { LifeBuoy, Send, Inbox } from "lucide-react";
import { cn } from "@/lib/utils";
import { createReport, fetchMyReports, ReportItem } from "@/lib/api";

const CATEGORIES = [
  { value: "bug", label: "Баг" },
  { value: "question", label: "Вопрос" },
  { value: "idea", label: "Идея" },
  { value: "other", label: "Другое" },
] as const;

const STATUS_LABEL: Record<string, string> = {
  new: "Новый",
  in_progress: "В работе",
  resolved: "Решён",
  rejected: "Отклонён",
};

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

/** Support window (centered): «Новый отчёт» form + «Мои отчёты» list with
 *  status. Reports land in RCC (admin panel) for processing. */
export function ReportModal({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<"new" | "mine">("new");
  const [category, setCategory] = useState<string>("bug");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [mine, setMine] = useState<ReportItem[]>([]);
  const [loadingMine, setLoadingMine] = useState(false);

  useEffect(() => {
    if (tab !== "mine") return;
    setLoadingMine(true);
    fetchMyReports()
      .then(setMine)
      .catch(() => setMine([]))
      .finally(() => setLoadingMine(false));
  }, [tab]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await createReport({
        category,
        subject,
        description,
        contact: contact.trim() || undefined,
      });
      setSent(true);
      setSubject("");
      setDescription("");
      setContact("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось отправить отчёт");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center md:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div className="relative flex flex-col w-full h-full md:max-w-xl md:max-h-[85vh] md:h-auto md:mx-4 md:rounded-2xl border border-strongline bg-app shadow-2xl overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 md:px-6 py-4 border-b border-hairline shrink-0">
          <h2 className="text-lg font-medium text-primary flex items-center gap-2">
            <LifeBuoy size={18} className="text-accent" /> Поддержка
          </h2>
          <div className="flex items-center gap-1">
            <TabButton active={tab === "new"} onClick={() => setTab("new")}>
              <Send size={13} /> Новый отчёт
            </TabButton>
            <TabButton active={tab === "mine"} onClick={() => setTab("mine")}>
              <Inbox size={13} /> Мои отчёты
            </TabButton>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 md:px-6 py-5">
          {tab === "new" ? (
            <form onSubmit={submit} className="flex flex-col gap-4">
              {sent && (
                <div className="text-sm px-3 py-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
                  Отчёт отправлен. Статус можно посмотреть во вкладке «Мои отчёты».
                </div>
              )}
              {/* Category */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setCategory(c.value)}
                    className={cn(
                      "h-9 rounded-lg text-sm border transition-colors",
                      category === c.value
                        ? "border-accent bg-accent/10 text-primary"
                        : "border-hairline bg-hoverbg text-secondary hover:bg-hoverbg2"
                    )}
                  >
                    {c.label}
                  </button>
                ))}
              </div>

              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Тема (коротко: что случилось)"
                maxLength={255}
                required
                minLength={3}
                className="w-full bg-elevated border border-strongline rounded-xl px-4 py-2.5 text-sm text-primary placeholder:text-tertiary focus:outline-none focus:border-accent/50 transition-colors"
              />
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Опишите проблему: что делали, что ожидали, что получилось. Чем подробнее — тем быстрее поможем."
                rows={6}
                maxLength={5000}
                required
                minLength={3}
                className="w-full bg-elevated border border-strongline rounded-xl px-4 py-3 text-sm text-primary placeholder:text-tertiary focus:outline-none focus:border-accent/50 transition-colors resize-none"
              />
              <input
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder="Контакт для ответа (необязательно)"
                maxLength={255}
                className="w-full bg-elevated border border-strongline rounded-xl px-4 py-2.5 text-sm text-primary placeholder:text-tertiary focus:outline-none focus:border-accent/50 transition-colors"
              />

              {error && <div className="text-sm text-red-400 px-1">{error}</div>}

              <button
                type="submit"
                disabled={busy || !subject.trim() || !description.trim()}
                className="h-10 rounded-xl bg-accent text-accent-contrast text-sm font-medium flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <Send size={14} /> {busy ? "Отправка..." : "Отправить отчёт"}
              </button>
            </form>
          ) : (
            <div className="space-y-2">
              {loadingMine && <div className="text-sm text-tertiary text-center py-8">Загрузка...</div>}
              {!loadingMine && mine.length === 0 && (
                <div className="text-sm text-tertiary text-center py-10">
                  Вы ещё не отправляли отчёты.
                </div>
              )}
              {mine.map((r) => (
                <div key={r.id} className="rounded-xl border border-hairline bg-hoverbg/40 p-4 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <CategoryBadge category={r.category} />
                      <span className="text-sm text-primary truncate">{r.subject}</span>
                    </div>
                    <StatusBadge status={r.status} />
                  </div>
                  <p className="text-xs text-secondary whitespace-pre-wrap line-clamp-3">{r.description}</p>
                  <div className="text-[11px] text-tertiary">
                    {fmtDate(r.created_at)}
                    {r.contact ? ` · ${r.contact}` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "h-8 px-3 rounded-lg text-xs flex items-center gap-1.5 transition-colors",
        active ? "bg-hoverbg2 text-primary" : "text-tertiary hover:text-primary"
      )}
    >
      {children}
    </button>
  );
}

function CategoryBadge({ category }: { category: string }) {
  return (
    <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded border border-hairline text-tertiary uppercase tracking-wide">
      {CATEGORIES.find((c) => c.value === category)?.label ?? category}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={cn(
        "shrink-0 text-[11px] px-2 py-0.5 rounded-full border",
        status === "resolved"
          ? "border-emerald-500/40 text-emerald-400"
          : status === "in_progress"
            ? "border-amber-500/40 text-amber-400"
            : status === "rejected"
              ? "border-hairline text-tertiary"
              : "border-red-500/40 text-red-400"
      )}
    >
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}
